import React from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatCurrency, getCommissionRates } from "../utils/commissionCalculator";

/**
 * PaymentBreakdown Component
 * Shows payment calculation with commission and late fees
 *
 * @param {number} baseAmount - Base payment amount (rent/advance)
 * @param {number} commissionAmount - OTS commission amount
 * @param {string} commissionPercentage - Commission percentage string (e.g., "3%")
 * @param {number} lateFee - Late payment fee (0 if not late)
 * @param {number} totalAmount - Total amount tenant pays
 * @param {number} landlordReceives - Amount landlord receives
 * @param {string} type - 'advance' or 'rent'
 * @param {boolean} isLate - Whether payment is late
 * @param {number} daysOverdue - Number of days overdue
 * @param {boolean} compact - Compact mode for smaller displays
 */
const PaymentBreakdown = ({
  baseAmount = 0,
  commissionAmount = 0,
  commissionPercentage = "0%",
  lateFee = 0,
  totalAmount = 0,
  landlordReceives = 0,
  type = "rent",
  isLate = false,
  daysOverdue = 0,
  compact = false,
  lateFeeAmount = null, // Optional: will use dynamic rates if not provided
  gracePeriod = null, // Optional: will use dynamic rates if not provided
}) => {
  // Get current commission rates (dynamically updated)
  const rates = getCommissionRates();
  const displayLateFee = lateFeeAmount ?? rates.LATE_FEE ?? 0;
  const displayGracePeriod = gracePeriod ?? rates.GRACE_PERIOD ?? 5;

  const isAdvance = type === "advance";
  const title = isAdvance ? "Payment" : "Monthly Rent";
  const commissionLabel = isAdvance
    ? `OTS Service Fee (${commissionPercentage})`
    : `OTS Service Fee (${commissionPercentage})`;

  if (compact) {
    return (
      <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 2, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {isAdvance ? "Advance" : "Rent"}:
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formatCurrency(baseAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Service Fee ({commissionPercentage}):
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formatCurrency(commissionAmount)}
          </Typography>
        </Box>
        {lateFee > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="error">
              Late Fee:
            </Typography>
            <Typography variant="body2" fontWeight={500} color="error">
              {formatCurrency(lateFee)}
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body1" fontWeight={600}>
            Total:
          </Typography>
          <Typography variant="body1" fontWeight={700} color="primary">
            {formatCurrency(totalAmount)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 3, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: isAdvance ? "primary.main" : "success.main",
          color: "white",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ReceiptIcon />
        <Typography variant="h6" fontWeight={600}>
          {title} Breakdown
        </Typography>
      </Box>

      {/* Late Payment Warning */}
      {isLate && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ borderRadius: 0 }}
        >
          Payment is {daysOverdue} days overdue. Late fee of {formatCurrency(displayLateFee)} applied.
        </Alert>
      )}

      {/* Breakdown Items */}
      <Box sx={{ p: 3 }}>
        {/* Base Amount */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon color="action" fontSize="small" />
            <Typography variant="body1">
              {isAdvance ? "Security Advance" : "Monthly Rent"}
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight={600}>
            {formatCurrency(baseAmount)}
          </Typography>
        </Box>

        {/* Commission */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="body1">{commissionLabel}</Typography>
            <Typography variant="caption" color="text.secondary">
              Platform service charge
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight={600} color="primary">
            + {formatCurrency(commissionAmount)}
          </Typography>
        </Box>

        {/* Late Fee (if applicable) */}
        {lateFee > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography variant="body1" color="error">
                Late Payment Fee
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Applied after {displayGracePeriod} day grace period
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600} color="error">
              + {formatCurrency(lateFee)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Total */}
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f5f5f5",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Total Payable
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary">
            {formatCurrency(totalAmount)}
          </Typography>
        </Box> */}

        {/* Landlord Receives Info */}
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mt: 2,
            p: 1.5,
            bgcolor: "success.light",
            borderRadius: 2,
          }}
        >
          <CheckCircleIcon sx={{ color: "success.dark", fontSize: 20 }} />
          <Typography variant="body2" color="success.dark">
            Landlord receives: <strong>{formatCurrency(landlordReceives)}</strong>
          </Typography>
        </Box> */}

        {/* Commission Info Chips */}
        {/* <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2, justifyContent: "center" }}>
          <Chip
            size="small"
            label={`${commissionPercentage} Service Fee`}
            color="primary"
            variant="outlined"
          />
          {isLate && (
            <Chip
              size="small"
              label={`Rs ${displayLateFee} Late Fee`}
              color="error"
              variant="outlined"
            />
          )}
          <Chip
            size="small"
            label={`${displayGracePeriod} Days Grace`}
            color="info"
            variant="outlined"
          />
        </Box> */}
      </Box>
    </Paper>
  );
};

export default PaymentBreakdown;
